import React, { useState, useCallback } from 'react';
import { API_URL } from '../config';

const BatchUploadModal = ({ onClose, engineName, taskId, onSuccess }) => {
  const [files, setFiles] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const onDrop = useCallback((e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFilesSelected(e.dataTransfer.files);
    }
  }, []);

  const handleFilesSelected = async (selectedFiles) => {
    const fileArray = Array.from(selectedFiles);
    if(fileArray.length === 0) return;
    setFiles((prev) => [...prev, ...fileArray]);
    processFiles(fileArray);
  };

    const processFiles = async (fileArray) => {
    setIsProcessing(true);
    setErrorMsg('');
    
    const newPreviews = fileArray.map(file => ({
      fileName: file.name,
      objectUrl: URL.createObjectURL(file),
      status: 'processing',
      extractedText: 'AI 提取中...',
      matchedGeoResultId: null,
      matchedQuestion: '匹配中...',
      confidence: 0,
      serverUrl: null
    }));
    
    setPreviewData(prev => [...prev, ...newPreviews]);
    
    const token = localStorage.getItem('token');
    
    // Chunk files to avoid Cloudflare/Nginx 100MB payload limits
    const CHUNK_SIZE = 10;
    for (let i = 0; i < fileArray.length; i += CHUNK_SIZE) {
      const chunk = fileArray.slice(i, i + CHUNK_SIZE);
      const formData = new FormData();
      formData.append('taskId', taskId);
      if (engineName) {
        formData.append('engineId', engineName);
      }
      chunk.forEach(file => formData.append('screenshots', file));

      try {
        const response = await fetch(`${API_URL}/geo-tasks/batchMatchMb`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        
        const data = await response.json();
        if (data && data.results) {
          setPreviewData(prev => {
            const updated = [...prev];
            data.results.forEach((resItem, resIdx) => {
              const fileNameToMatch = chunk[resIdx]?.name || resItem.fileName;
              const idx = updated.findIndex(p => p.fileName === fileNameToMatch && p.status === 'processing');
              if (idx !== -1) {
                updated[idx] = { 
                  ...updated[idx], 
                  ...resItem, 
                  status: 'done',
                  serverUrl: resItem.objectUrl,
                  objectUrl: updated[idx].objectUrl,
                  fileName: updated[idx].fileName
                };
              }
            });
            return updated;
          });
        } else {
           setErrorMsg(data.error || '部分识别失败');
        }
      } catch (error) {
         console.error('Batch upload chunk error:', error);
         setErrorMsg('网络或服务器异常，部分图片可能未处理');
         // Mark remaining processing items in this chunk as error
         setPreviewData(prev => {
           const updated = [...prev];
           chunk.forEach(file => {
             const idx = updated.findIndex(p => p.fileName === file.name && p.status === 'processing');
             if (idx !== -1) updated[idx].status = 'error';
           });
           return updated;
         });
      }
    }
    
    setIsProcessing(false);
  };

  const handleSubmit = async () => {
    const mappings = previewData
      .filter(p => p.matchedGeoResultId && p.status === 'done' && p.serverUrl)
      .map(p => ({
        geoResultId: p.matchedGeoResultId,
        imagePath: p.serverUrl
      }));

    if (mappings.length === 0) {
      alert('没有有效的高置信度匹配结果可提交。');
      return;
    }

    setIsUploading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/geo-tasks/batchConfirmMb`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ mappings })
      });
      const data = await res.json();
      if (data.success) {
        onSuccess();
      } else {
        setErrorMsg(data.error || '提交失败');
      }
    } catch (error) {
      console.error(error);
      setErrorMsg('提交请求失败');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <div className="inline-block align-bottom bg-white dark:bg-slate-800 rounded-lg text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
          <div className="bg-white dark:bg-slate-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="modal-title">
              批量智能匹配上传 - {engineName}
            </h3>
            
            <div 
              className="mt-4 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg p-10 text-center cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/50"
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              onClick={() => document.getElementById('batchFileInput').click()}
            >
              <input 
                id="batchFileInput"
                type="file" 
                multiple 
                accept="image/*" 
                className="hidden" 
                onChange={(e) => handleFilesSelected(e.target.files)} 
              />
              <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {isProcessing ? <span className="text-indigo-600 font-bold">处理中，请稍候...</span> : "拖拽多张截图到这里，或点击选择文件"}
              </p>
            </div>
            
            {errorMsg && (
              <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
                {errorMsg}
              </div>
            )}

            {previewData.length > 0 && (
              <div className="mt-6 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
                   <thead>
                     <tr>
                       <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">截图</th>
                       <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">提取词汇</th>
                       <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">系统匹配词</th>
                       <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">置信度</th>
                     </tr>
                   </thead>
                   <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                     {previewData.map((row, i) => (
                       <tr key={i} className={row.status === 'processing' ? 'opacity-50' : ''}>
                         <td className="px-4 py-2 whitespace-nowrap">
                           <img src={row.objectUrl} alt="preview" className="h-16 w-16 object-cover rounded" />
                         </td>
                         <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-300 max-w-xs truncate" title={row.extractedText}>
                           {row.extractedText}
                         </td>
                         <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-300 max-w-xs truncate" title={row.matchedQuestion}>
                           {row.matchedQuestion || <span className="text-red-500">无匹配</span>}
                         </td>
                         <td className="px-4 py-2 text-sm whitespace-nowrap">
                           {row.status === 'processing' ? (
                             <span className="text-gray-500">识别中...</span>
                           ) : (
                             <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${row.confidence > 80 ? 'bg-green-100 text-green-800' : row.confidence > 60 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                               {row.confidence}% 匹配
                             </span>
                           )}
                         </td>
                       </tr>
                     ))}
                   </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="bg-gray-50 dark:bg-slate-700/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse rounded-b-lg">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
              disabled={isUploading || isProcessing || previewData.length === 0}
              onClick={handleSubmit}
            >
              {isUploading ? '提交中...' : '确认并提交分析'}
            </button>
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-slate-600 shadow-sm px-4 py-2 bg-white dark:bg-slate-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              取消
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BatchUploadModal;
